// Display helpers for the HR & People module, ported from the Stephanet reference (src/format.js).

const money = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 });
const number = new Intl.NumberFormat('en-NG', { maximumFractionDigits: 2 });

export const has = v => v !== null && v !== undefined && v !== '';
export const fmtMoney = v => (has(v) ? money.format(Number(v)) : '—');
export const fmtNumber = v => (has(v) ? number.format(Number(v)) : '—');

// in_progress -> In Progress; values already capitalised are kept as typed
export const titleCase = s => {
  const text = String(s ?? '').replace(/_/g, ' ');
  return /[A-Z]/.test(text) ? text : text.replace(/(^|\s)\S/g, c => c.toUpperCase());
};

export const today = () => new Date().toISOString().slice(0, 10);
export const fmtDate = d => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');
export const fmtDateTime = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
export const daysUntil = d => (d ? Math.ceil((new Date(`${String(d).slice(0, 10)}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null);
export const monthsSince = d => {
  if (!d) return null;
  const a = new Date(`${String(d).slice(0, 10)}T00:00:00`), b = new Date();
  return Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth() - (b.getDate() < a.getDate() ? 1 : 0));
};
export const initials = n => String(n || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

// value of one register field, by its type
export function fmtValue(field, row) {
  if (!field) return '';
  const v = field.get ? field.get(row) : row[field.name];
  if (!has(v)) return '—';
  switch (field.type) {
    case 'money': return fmtMoney(v);
    case 'number': case 'integer': return fmtNumber(v);
    case 'boolean': return v ? 'Yes' : 'No';
    case 'date': return fmtDate(v);
    case 'datetime': return fmtDateTime(v);
    case 'select': return titleCase(v);
    default: return String(v);
  }
}

export function toCSV(columns, rows) {
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [columns.map(c => esc(c.label)).join(','), ...rows.map(r => columns.map(c => esc(c.get(r))).join(','))].join('\n');
}

export function download(filename, text) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  a.download = filename;
  a.click();
}

const STATUS_TONE = {
  active: 'good', approved: 'good', completed: 'good', paid: 'good', available: 'good', issued: 'good', hired: 'good', present: 'good',
  verified: 'good', valid: 'good', enrolled: 'good', occupied: 'info', printed: 'info',
  pending: 'warn', pre_approved: 'info', submitted: 'warn', draft: 'muted', onboarding: 'info', probation: 'warn', requested: 'warn',
  applied: 'info', screening: 'warn', interview: 'warn', offered: 'info', half_day: 'warn', late: 'warn',
  rejected: 'bad', suspended: 'bad', expired: 'bad', absent: 'bad', revoked: 'muted', lost: 'bad',
  exited: 'muted', inactive: 'muted', cancelled: 'muted', vacated: 'muted', closed: 'muted', leave: 'info', sick: 'info', holiday: 'muted', off: 'muted',
};
export const statusTone = s => STATUS_TONE[String(s || '').toLowerCase()] || 'muted';

const VERBS = {
  approved: 'Approve', rejected: 'Reject', pre_approved: 'Pre-Approve', pending: 'Submit', draft: 'Back to Draft', cancelled: 'Cancel',
  active: 'Activate', suspended: 'Suspend', exited: 'Exit', inactive: 'Deactivate', probation: 'Move to Probation', onboarding: 'Move to Onboarding',
};
export const actionLabel = to => VERBS[to] || `Move to ${titleCase(to)}`;

// enum values shared with the backend (app/models/hr_model.py)
export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract', 'probation', 'casual', 'intern', 'consultant'];
export const EMPLOYMENT_STATUSES = ['draft', 'onboarding', 'probation', 'active', 'suspended', 'inactive', 'exited'];
export const SALARY_TYPES = ['monthly', 'daily', 'hourly'];
export const GENDERS = ['male', 'female', 'other'];
export const MARITAL_STATUSES = ['single', 'married', 'divorced', 'widowed', 'separated'];
export const WORKFLOW_STATUSES = ['draft', 'pending', 'pre_approved', 'approved', 'rejected', 'cancelled'];
export const OT_TYPES = ['weekday', 'weekend', 'holiday'];

// a time as the server sends it ("14:05", "02:09:25", "2026-11-04 14:05:00", "2026-11-04T14:05") in 12-hour form
// ("2:05 PM", "2:09:25 AM", "2026-11-04 2:05 PM"); anything else comes back unchanged
export const t12 = s => {
  if (!s) return s;
  const m = String(s).match(/^(\d{4}-\d{2}-\d{2}[ T])?(\d{1,2}):(\d{2})(:\d{2})?/);
  if (!m) return s;
  const h = Number(m[2]);
  return `${m[1] ? `${m[1].slice(0, 10)} ` : ''}${h % 12 || 12}:${m[3]}${m[4] || ''} ${h < 12 ? 'AM' : 'PM'}`;
};

// every time of day inside a piece of text in 12-hour form: "08:00–17:00" → "8:00 AM–5:00 PM"
export const times12 = s => (s ? String(s).replace(/\b\d{1,2}:\d{2}(:\d{2})?\b/g, m => t12(m)) : s);

